import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiService } from '../core/services/api.service';
import {
  TaskStatus,
  CreateTaskStatus,
  UpdateTaskStatus,
  TaskAttribute,
  TaskAttributeValue,
  TaskAttributeType,
  CreateTaskAttribute,
  UpdateTaskAttribute,
  UpdateTaskAttributeFull,
  TaskAttributeOptionValue,
  TaskTag,
  CreateTaskTag,
  UpdateTaskTag,
  ProjectTask,
  TaskKanbanBoard,
  TaskStatistics,
  TaskFilter,
  TaskCreateUpdate,
  TaskStatusUpdate,
  TaskComment,
  TaskCommentCreate,
  TaskWatcher,
  TaskWatcherManage,
  UserDetail
} from '../models/task-management.models';

@Injectable({
  providedIn: 'root'
})
export class TaskManagementService {
  private readonly baseUrl = 'taskmanagement';

  constructor(private apiService: ApiService) { }

  // Kanban Board Operations
  getKanbanBoard(blockScheduleId: number, filter?: TaskFilter): Observable<TaskKanbanBoard> {
    let url = `${this.baseUrl}/kanban/${blockScheduleId}`;
    if (filter) {
      const params = new URLSearchParams();
      Object.entries(filter).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          if (Array.isArray(value)) {
            value.forEach(v => params.append(key, v.toString()));
          } else {
            params.append(key, value.toString());
          }
        }
      });
      if (params.toString()) {
        url += `?${params.toString()}`;
      }
    }
    return this.apiService.get(url);
  }

  getProjectKanbanBoard(projectId: number): Observable<TaskKanbanBoard>;
  getProjectKanbanBoard(
    projectId: number, 
    blockScheduleId?: number, 
    projectTemplateId?: number, 
    filter?: TaskFilter
  ): Observable<TaskKanbanBoard>;
  getProjectKanbanBoard(
    projectId: number, 
    blockScheduleId?: number, 
    projectTemplateId?: number, 
    filter?: TaskFilter
  ): Observable<TaskKanbanBoard> {
    let url = `${this.baseUrl}/project/${projectId}/kanban`;
    const params = new URLSearchParams();
    
    // Only add parameters if they are provided and not undefined
    if (blockScheduleId !== undefined && blockScheduleId !== null && blockScheduleId > 0) {
      params.append('blockScheduleId', blockScheduleId.toString());
    }
    
    if (projectTemplateId !== undefined && projectTemplateId !== null && projectTemplateId > 0) {
      params.append('projectTemplateId', projectTemplateId.toString());
    }
    
    if (filter) {
      Object.entries(filter).forEach(([key, value]) => {
        // Skip blockScheduleId and projectTemplateId in filter to avoid duplicates
        if (key === 'blockScheduleId' || key === 'projectTemplateId') {
          return;
        }
        
        if (value !== undefined && value !== null) {
          if (Array.isArray(value)) {
            value.forEach(v => params.append(key, v.toString()));
          } else {
            params.append(key, value.toString());
          }
        }
      });
    }
    
    if (params.toString()) {
      url += `?${params.toString()}`;
    }
    
    return this.apiService.get(url);
  }

  getTaskStatistics(blockScheduleId: number, filter?: TaskFilter): Observable<TaskStatistics> {
    let url = `${this.baseUrl}/statistics/${blockScheduleId}`;
    if (filter) {
      const params = new URLSearchParams();
      Object.entries(filter).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          if (Array.isArray(value)) {
            value.forEach(v => params.append(key, v.toString()));
          } else {
            params.append(key, value.toString());
          }
        }
      });
      if (params.toString()) {
        url += `?${params.toString()}`;
      }
    }
    return this.apiService.get(url);
  }

  // Task CRUD Operations
  getTaskById(taskId: number): Observable<ProjectTask> {
    return this.apiService.get(`${this.baseUrl}/task/${taskId}`);
  }

  getTasksByBlockSchedule(blockScheduleId: number, filter?: TaskFilter): Observable<ProjectTask[]> {
    let url = `${this.baseUrl}/tasks/${blockScheduleId}`;
    if (filter) {
      const params = new URLSearchParams();
      Object.entries(filter).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          if (Array.isArray(value)) {
            value.forEach(v => params.append(key, v.toString()));
          } else {
            params.append(key, value.toString());
          }
        }
      });
      if (params.toString()) {
        url += `?${params.toString()}`;
      }
    }
    return this.apiService.get(url);
  }

  getTasksByProject(projectId: number): Observable<ProjectTask[]> {
    return this.apiService.get(`${this.baseUrl}/project/${projectId}`);
  }

  createTask(model: TaskCreateUpdate): Observable<{taskId: number, message: string}> {
    return this.apiService.post(`${this.baseUrl}/tasks`, model);
  }

  updateTask(taskId: number, model: TaskCreateUpdate): Observable<{message: string}> {
    return this.apiService.put(`${this.baseUrl}/task/${taskId}`, model);
  }

  updateTaskStatus(taskId: number, model: TaskStatusUpdate): Observable<{message: string}> {
    return this.apiService.put(`${this.baseUrl}/task/${taskId}/status`, model);
  }

  deleteTask(taskId: number): Observable<{message: string}> {
    return this.apiService.delete(`${this.baseUrl}/task/${taskId}`);
  }

  // Task Hierarchy Operations
  getSubTasks(taskId: number): Observable<ProjectTask[]> {
    return this.apiService.get(`${this.baseUrl}/task/${taskId}/subtasks`);
  }

  getTaskHierarchy(blockScheduleId: number): Observable<ProjectTask[]> {
    return this.apiService.get(`${this.baseUrl}/hierarchy/${blockScheduleId}`);
  }

  moveTaskToParent(taskId: number, newParentId?: number): Observable<{message: string}> {
    return this.apiService.put(`${this.baseUrl}/task/${taskId}/parent`, newParentId);
  }

  // Tag Operations
  getAllTags(): Observable<TaskTag[]> {
    return this.apiService.get(`${this.baseUrl}/tags`);
  }

  getTaskTags(taskId: number): Observable<TaskTag[]> {
    return this.apiService.get(`${this.baseUrl}/task/${taskId}/tags`);
  }

  assignTaskTags(taskId: number, tagIds: number[]): Observable<{message: string}> {
    return this.apiService.post(`${this.baseUrl}/task/${taskId}/tags`, tagIds);
  }

  removeTaskTag(taskId: number, tagId: number): Observable<{message: string}> {
    return this.apiService.delete(`${this.baseUrl}/task/${taskId}/tag/${tagId}`);
  }

  // Watcher Operations
  getTaskWatchers(taskId: number): Observable<number[]> {
    return this.apiService.get(`${this.baseUrl}/task/${taskId}/watchers`);
  }

  addTaskWatchers(taskId: number, userIds: number[]): Observable<{message: string}> {
    return this.apiService.post(`${this.baseUrl}/task/${taskId}/watchers`, userIds);
  }

  removeTaskWatcher(taskId: number, userId: number): Observable<{message: string}> {
    return this.apiService.delete(`${this.baseUrl}/task/${taskId}/watcher/${userId}`);
  }

  // Search Operations
  searchTasks(blockScheduleId: number, searchText: string, filter?: TaskFilter): Observable<ProjectTask[]> {
    let url = `${this.baseUrl}/search/${blockScheduleId}?searchText=${encodeURIComponent(searchText)}`;
    if (filter) {
      const params = new URLSearchParams();
      Object.entries(filter).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          if (Array.isArray(value)) {
            value.forEach(v => params.append(key, v.toString()));
          } else {
            params.append(key, value.toString());
          }
        }
      });
      if (params.toString()) {
        url += `&${params.toString()}`;
      }
    }
    return this.apiService.get(url);
  }

  // Bulk Operations
  bulkUpdateTaskStatus(taskIds: number[], statusId: number): Observable<{message: string}> {
    return this.apiService.put(`${this.baseUrl}/tasks/status?statusId=${statusId}`, taskIds);
  }

  bulkAssignTasks(taskIds: number[], assignedTo: number): Observable<{message: string}> {
    return this.apiService.put(`${this.baseUrl}/tasks/assign?assignedTo=${assignedTo}`, taskIds);
  }

  bulkDeleteTasks(taskIds: number[]): Observable<{message: string}> {
    return this.apiService.delete(`${this.baseUrl}/tasks/bulk?taskIds=${taskIds.join(',')}`);
  }

  // Template Integration
  getTasksByTemplate(projectTemplateId: number): Observable<ProjectTask[]> {
    return this.apiService.get(`${this.baseUrl}/template/${projectTemplateId}/tasks`);
  }

  createTasksFromTemplate(projectTemplateId: number, blockScheduleId: number): Observable<{message: string}> {
    return this.apiService.post(`${this.baseUrl}/template/${projectTemplateId}/create-tasks/${blockScheduleId}`, {});
  }

  // Lookup Data
  getProjectUsers(blockScheduleId: number): Observable<UserDetail[]> {
    return this.apiService.get(`${this.baseUrl}/users/${blockScheduleId}`);
  }

  getTaskAttributes(): Observable<TaskAttribute[]> {
    return this.apiService.get(`${this.baseUrl}/attributes`);
  }

  // Task Status (existing methods)
  getAllTaskStatuses(): Observable<TaskStatus[]> {
    return this.apiService.get(`${this.baseUrl}/statuses`);
  }

  getTaskStatusById(statusId: number): Observable<TaskStatus> {
    return this.apiService.get(`${this.baseUrl}/statuses/${statusId}`);
  }

  createTaskStatus(model: CreateTaskStatus): Observable<TaskStatus> {
    return this.apiService.post(`${this.baseUrl}/statuses`, model);
  }

  updateTaskStatusDefinition(model: UpdateTaskStatus): Observable<TaskStatus> {
    return this.apiService.put(`${this.baseUrl}/statuses`, model);
  }

  deleteTaskStatus(statusId: number): Observable<void> {
    return this.apiService.delete(`${this.baseUrl}/statuses/${statusId}`);
  }

  reorderTaskStatuses(statusIds: number[]): Observable<void> {
    return this.apiService.put(`${this.baseUrl}/statuses/reorder`, statusIds);
  }

  // Task Attributes (existing methods)
  getAllTaskAttributes(): Observable<TaskAttribute[]> {
    return this.apiService.get(`${this.baseUrl}/attributes`);
  }

  getTaskAttributeById(attributeId: number): Observable<TaskAttribute> {
    return this.apiService.get(`${this.baseUrl}/attributes/${attributeId}`);
  }

  createTaskAttribute(model: CreateTaskAttribute): Observable<TaskAttribute> {
    return this.apiService.post(`${this.baseUrl}/attributes`, model);
  }

  updateTaskAttribute(model: UpdateTaskAttribute): Observable<TaskAttribute> {
    return this.apiService.put(`${this.baseUrl}/attributes`, model);
  }

  deleteTaskAttribute(attributeId: number): Observable<void> {
    return this.apiService.delete(`${this.baseUrl}/attributes/${attributeId}`);
  }

  reorderTaskAttributes(attributeIds: number[]): Observable<void> {
    return this.apiService.put(`${this.baseUrl}/attributes/reorder`, attributeIds);
  }

  updateTaskAttributeFull(model: UpdateTaskAttributeFull): Observable<TaskAttribute> {
    return this.apiService.put(`${this.baseUrl}/attributes/full`, model);
  }

  getTaskAttributeOptions(attributeId: number): Observable<TaskAttributeOptionValue[]> {
    return this.apiService.get(`${this.baseUrl}/attributes/${attributeId}/options`);
  }

  getTaskAttributeValues(taskId: number): Observable<TaskAttributeValue[]> {
    return this.apiService.get(`${this.baseUrl}/task/${taskId}/attributes`);
  }

  // Task Tags (existing methods)
  getAllTaskTags(): Observable<TaskTag[]> {
    return this.apiService.get(`${this.baseUrl}/tags`);
  }

  getTaskTagById(tagId: number): Observable<TaskTag> {
    return this.apiService.get(`${this.baseUrl}/tags/${tagId}`);
  }

  createTaskTag(model: CreateTaskTag): Observable<TaskTag> {
    return this.apiService.post(`${this.baseUrl}/tags`, model);
  }

  updateTaskTag(model: UpdateTaskTag): Observable<TaskTag> {
    return this.apiService.put(`${this.baseUrl}/tags`, model);
  }

  deleteTaskTag(tagId: number): Observable<void> {
    return this.apiService.delete(`${this.baseUrl}/tags/${tagId}`);
  }

  // Task Attributes
  getAllTaskAttributeTypes(): Observable<TaskAttributeType[]> {
    return this.apiService.get(`${this.baseUrl}/attribute-types`);
  }

  // Task Comments
  getTaskComments(taskId: number): Observable<TaskComment[]> {
    return this.apiService.get(`${this.baseUrl}/task/${taskId}/comments`);
  }

  addTaskComment(model: TaskCommentCreate, userId: number): Observable<{ commentId: number; message: string }> {
    return this.apiService.post(`${this.baseUrl}/task/${model.taskId}/comment/${userId}`, model);
  }

  updateTaskComment(commentId: number, model: { commentText: string }): Observable<{ message: string }> {
    return this.apiService.put(`${this.baseUrl}/task/comment/${commentId}`, model);
  }

  deleteTaskComment(commentId: number): Observable<{ message: string }> {
    return this.apiService.delete(`${this.baseUrl}/task/comment/${commentId}`);
  }

  // Comment Attachments
  addCommentAttachment(commentId: number, file: File): Observable<{ attachmentId: number; fileName: string; message: string }> {
    const formData = new FormData();
    formData.append('file', file);
    return this.apiService.post(`${this.baseUrl}/task/comment/${commentId}/attachments`, formData);
  }

  deleteCommentAttachment(attachmentId: number): Observable<{ message: string }> {
    return this.apiService.delete(`${this.baseUrl}/task/comment/attachment/${attachmentId}`);
  }

  downloadCommentAttachment(attachmentId: number): Observable<Blob> {
    return this.apiService.getBlob(`${this.baseUrl}/task/comment/attachment/${attachmentId}/download`);
  }

}
